import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const Contact: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // 3-stage transition: Dark -> White -> Dark
  const backgroundColor = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], ["#09090b", "#fafaf9", "#fafaf9", "#09090b"]);
  const textColor = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], ["#ffffff", "#18181b", "#18181b", "#ffffff"]);
  const borderColor = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], ["rgba(255,255,255,0.1)", "rgba(24,24,27,1)", "rgba(24,24,27,1)", "rgba(255,255,255,0.1)"]);
  const subTextColor = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], ["#a1a1aa", "#52525b", "#52525b", "#a1a1aa"]);

  return (
    <motion.section
      id="contact"
      ref={containerRef}
      style={{ backgroundColor, color: textColor }}
      className="pt-[40vh] md:pt-[80vh] pb-16 md:pb-32 px-4 md:px-12 lg:px-24 relative min-h-[200vh] md:min-h-[300vh] flex flex-col justify-start"
    >
      {/* Header */}
      <motion.div
        style={{ borderColor: borderColor }}
        className="mb-16 md:mb-24 border-b pb-8 md:pb-12"
      >
        <h2 className="text-[12vw] md:text-[8vw] leading-[0.8] font-serif font-black tracking-tighter">
          GET IN<br />TOUCH
        </h2>
      </motion.div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 max-w-7xl">
        {/* Left: Intro Text */}
        <div>
          <h3 className="text-4xl md:text-6xl font-serif font-medium mb-6">Speak to me.</h3>
          <motion.p style={{ color: subTextColor }} className="text-base md:text-md leading-relaxed">
            If you want to start a project, share an idea or project requirements,<br />I want to hear from you.
          </motion.p>
        </div>

        {/* Right: Contact Form */}
        <div>
          <form className="flex flex-col gap-8">
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest mb-2 block" style={{ color: subTextColor.get() }}>
                  First name
                </label>
                <input
                  type="text"
                  placeholder="Lewis"
                  className="w-full bg-transparent border-b border-current pb-3 text-xl md:text-2xl font-serif focus:outline-none transition-colors"
                  style={{ borderColor: borderColor.get() }}
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest mb-2 block" style={{ color: subTextColor.get() }}>
                  Last name
                </label>
                <input
                  type="text"
                  placeholder="Hamilton"
                  className="w-full bg-transparent border-b border-current pb-3 text-xl md:text-2xl font-serif focus:outline-none transition-colors"
                  style={{ borderColor: borderColor.get() }}
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest mb-2 block" style={{ color: subTextColor.get() }}>
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="lewis44@email.com"
                  className="w-full bg-transparent border-b border-current pb-3 text-xl md:text-2xl font-serif focus:outline-none transition-colors"
                  style={{ borderColor: borderColor.get() }}
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest mb-2 block" style={{ color: subTextColor.get() }}>
                  Phone number
                </label>
                <input
                  type="tel"
                  placeholder="+60"
                  className="w-full bg-transparent border-b border-current pb-3 text-xl md:text-2xl font-serif focus:outline-none transition-colors"
                  style={{ borderColor: borderColor.get() }}
                />
              </div>
            </div>

            {/* Project Details */}
            <div>
              <label className="text-xs font-mono uppercase tracking-widest mb-2 block" style={{ color: subTextColor.get() }}>
                Project details
              </label>
              <textarea
                placeholder="Tell me about your project..."
                rows={3}
                className="w-full bg-transparent border-b border-current pb-3 text-xl md:text-2xl font-serif focus:outline-none resize-none transition-colors"
                style={{ borderColor: borderColor.get() }}
              />
            </div>

            {/* Submit Button */}
            <div className="mt-4">
              <button
                type="submit"
                className="group relative px-8 py-4 bg-black text-white rounded-full font-mono text-sm uppercase tracking-widest hover:bg-zinc-800 transition-all duration-300 flex items-center gap-3"
              >
                Send Enquiry
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.section>
  );
};

export default Contact;
